import { VITE_SOLANA_VALIDATOR_ADDRESS } from '@/env'
import { Button } from '@/ui-kit'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  StakeProgram,
  TransactionMessage,
  VersionedTransaction
} from '@solana/web3.js'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { useFetchUserWallet } from '../../api'
import { ConnectWalletButton } from '../ConnectWalletButton'
import { LinkWalletButton } from '../LinkWalletButton'

type StakeButtonProps = {
  stakeAmount: string
  setStakeAmount: (amount: string) => void
  onSuccess: () => void
}

export const StakeButton = ({ stakeAmount, onSuccess, setStakeAmount }: StakeButtonProps) => {
  const { connection } = useConnection()
  const { publicKey, signTransaction } = useWallet()
  const { data: userWallet } = useFetchUserWallet()
  const [isLoading, setIsLoading] = useState(false)

  const handleStake = useCallback(async () => {
    if (!publicKey || !stakeAmount) return
    setIsLoading(true)
    try {
      const lamports = Number.parseFloat(stakeAmount) * LAMPORTS_PER_SOL
      const stakeKeypair = Keypair.generate()
      const validatorPubkey = new PublicKey(VITE_SOLANA_VALIDATOR_ADDRESS)

      const minimumRent = await connection.getMinimumBalanceForRentExemption(StakeProgram.space)
      const totalLamports = lamports + minimumRent

      // Create and initialize stake account
      const createStakeAccountTx = StakeProgram.createAccount({
        fromPubkey: publicKey,
        stakePubkey: stakeKeypair.publicKey,
        authorized: {
          staker: publicKey,
          withdrawer: publicKey
        },
        lamports: totalLamports
      })

      // Delegate stake to validator
      createStakeAccountTx.add(
        StakeProgram.delegate({
          stakePubkey: stakeKeypair.publicKey,
          authorizedPubkey: publicKey,
          votePubkey: validatorPubkey
        })
      )

      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash()
      createStakeAccountTx.recentBlockhash = blockhash
      createStakeAccountTx.feePayer = publicKey

      // Simulate before sending to catch errors early
      const messageV0 = new TransactionMessage({
        payerKey: publicKey,
        recentBlockhash: blockhash,
        instructions: createStakeAccountTx.instructions
      }).compileToLegacyMessage()
      const versionedTx = new VersionedTransaction(messageV0)
      const simulation = await connection.simulateTransaction(versionedTx, { sigVerify: false })
      if (simulation.value.err) {
        throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}`)
      }

      if (!signTransaction) throw new Error('Wallet does not support signTransaction')
      const signedTx = await signTransaction(createStakeAccountTx)
      signedTx.partialSign(stakeKeypair)
      const signature = await connection.sendRawTransaction(signedTx.serialize())
      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed')

      toast.success(`Staked ${stakeAmount} SOL successfully!`)
      setStakeAmount('')
      onSuccess()
    } catch (error) {
      console.error('Staking failed:', error)
      toast.error(error instanceof Error ? error.message : 'Staking failed')
    } finally {
      setIsLoading(false)
    }
  }, [publicKey, stakeAmount, connection, signTransaction, onSuccess, setStakeAmount])

  const isStakeDisabled =
    isLoading ||
    !stakeAmount ||
    Number.parseFloat(stakeAmount) <= 0 ||
    publicKey?.toString() !== userWallet?.wallet?.walletAddress


  return !userWallet?.wallet?.walletAddress ? (
    <LinkWalletButton className="bg-orange! relative flex h-[56px] w-full items-center justify-center gap-2 px-6 text-[18px] transition-all duration-300 select-none" />
  ) : !publicKey ? (
    <ConnectWalletButton className="bg-orange! relative flex h-[56px] w-full items-center justify-center gap-2 px-6 text-[18px] transition-all duration-300 select-none" />
  ) : (
    <Button type="button" onClick={handleStake} isDisabled={isStakeDisabled}>
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          Processing...
        </span>
      ) : userWallet?.wallet?.walletAddress &&
        publicKey &&
        publicKey?.toString() !== userWallet?.wallet?.walletAddress ? (
        'Wallet mismatch'
      ) : (
        'Stake SOL'
      )}
    </Button>
  )
}
